/** Translations of the analytics area; wired up in lib/i18n/en.ts. */
export const analyticsEn = {
  title: 'Analytics',
  subtitle: 'Revenue, contribution margin and customer structure over freely selectable periods.',
  tabs: {
    overview: 'Overview',
    customers: 'Customers',
    products: 'Products',
    geo: 'Regions',
    employeePerformance: 'Employee performance',
  },
  filters: {
    preset: 'Period',
    dateFrom: 'From',
    dateTo: 'To',
    comparison: 'Comparison',
    granularity: 'Granularity',
    export: 'CSV export',
    presets: {
      last30Days: 'Last 30 days',
      currentMonth: 'Current month',
      lastMonth: 'Previous month',
      currentQuarter: 'Current quarter',
      currentYear: 'Current year',
      lastYear: 'Previous year',
      custom: 'Custom',
    },
    comparisons: {
      NONE: 'No comparison',
      PREVIOUS_PERIOD: 'Previous period',
      PREVIOUS_YEAR: 'Same period last year',
    },
    granularities: {
      DAY: 'Day',
      WEEK: 'Week',
      MONTH: 'Month',
      QUARTER: 'Quarter',
    },
  },
  validation: {
    invalidRange: 'The start date must be before the end date.',
  },
  states: {
    error: 'The analysis could not be loaded.',
    empty: 'No data in the selected period',
    emptyHint: 'Adjust the period or record documents to see analytics.',
  },
  export: {
    success: 'The export has been created.',
  },
  kpi: {
    netRevenue: 'Net revenue',
    grossProfit: 'Gross profit',
    marginPct: 'Margin',
    averageOrderValue: 'Avg. order value',
    invoiceCount: 'Invoices',
    activeCustomers: 'Active customers',
    openReceivables: 'Open receivables',
    cancelledOrderRatePct: 'Cancellation rate',
    comparedToPrevious: 'compared to the comparison period',
  },
  charts: {
    revenueTitle: 'Revenue trend',
    netRevenue: 'Net revenue',
    grossProfit: 'Gross profit',
    orderIntake: 'Order intake',
    comparisonRevenue: 'Comparison period',
  },
  payment: {
    title: 'Invoice payment status',
    paid: 'Paid',
    open: 'Open',
    overdue: 'Overdue',
  },
  customers: {
    abcClass: 'Class {{abcClass}}',
    abcSummary: '{{count}} customers · {{share}} revenue share',
    atRisk: 'At risk of churn',
    industryTitle: 'Revenue by industry',
    tableTitle: 'Customer analysis',
    columns: {
      customer: 'Customer',
      industry: 'Industry',
      netRevenue: 'Net revenue',
      grossProfit: 'Gross profit',
      margin: 'Margin',
      orders: 'Orders',
      lastOrder: 'Last order',
      abc: 'ABC',
      churn: 'Churn',
    },
    churn: {
      NONE: 'Healthy',
      WARNING: 'Watch',
      CRITICAL: 'Critical',
    },
  },
  products: {
    groupTitle: 'Contribution margin I by product group',
    tableTitle: 'Product analysis',
    slowMoverTitle: 'Slow movers',
    totals: {
      netRevenue: 'Net revenue',
      costOfGoods: 'Cost of goods',
      contributionMargin1: 'Contribution margin I',
      productCount: 'Products',
    },
    columns: {
      product: 'Product',
      group: 'Product group',
      quantity: 'Quantity',
      netRevenue: 'Net revenue',
      contributionMargin1: 'CM I',
      contributionMargin2: 'CM II',
      margin: 'Margin',
      returnRate: 'Return rate',
      stockQuantity: 'Stock',
      stockValue: 'Stock value',
      lastMovement: 'Last movement',
      daysSinceMovement: 'Days without movement',
    },
  },
  geo: {
    regionTitle: 'Revenue by region',
    regionTableTitle: 'Regions',
    countryTitle: 'Revenue by country',
    regions: {
      DOMESTIC: 'Domestic',
      EU: 'EU',
      THIRD_COUNTRY: 'Non-EU',
    },
    columns: {
      region: 'Region',
      country: 'Country',
      netRevenue: 'Net revenue',
      documents: 'Documents',
      customers: 'Customers',
      share: 'Share',
    },
  },
  employeePerformance: {
    teamTitle: 'Employee performance team overview',
    teamSubtitle: 'Objective ERP, document and telephony metrics for the selected period.',
    updatedAt: 'Data as of {{date}} · Time zone {{timezone}}',
    emptyHint:
      'Adjust the employee or business filters. Employees without activity remain available.',
    minutes: '{{value}} min',
    noComparison: 'No meaningful comparison value',
    comparisonValue: 'Comparison value: {{value}}',
    showDefinition: 'Show definition for {{metric}}',
    chartAriaLabel: 'Timeline of revenue and order intake',
    telephonyChartAriaLabel: 'Timeline of CTI calls and call duration',
    chartDescription:
      'Chart with {{count}} time buckets. The values are also available in metrics and tables.',
    privacy: {
      title: 'Responsible use of employee data',
      description:
        'This analysis is purpose-bound to operational performance analysis. Metrics require business context and must not be the sole basis of automated employment decisions. Communication content, audio, transcripts, AI summaries, sentiment and personality characteristics are neither displayed nor evaluated.',
    },
    activation: {
      title: 'Employee performance is disabled',
      description:
        'The feature is disabled for this tenant. Enabling it does not store new performance raw data; it aggregates existing tenant-scoped ERP data.',
      legalReview:
        'Before production activation, review notice obligations, workplace policies, legal basis and any employee-representation participation rights.',
      enable: 'Enable after review',
      disable: 'Disable analysis',
      enabledSuccess: 'Employee performance has been enabled for this tenant.',
      disabledSuccess: 'Employee performance has been disabled for this tenant.',
    },
    status: {
      active: 'Active',
      inactive: 'Inactive',
      unassigned: 'Unassigned',
    },
    filters: {
      employees: 'Employees',
      allEmployees: 'All employees',
      selectedCount: '{{count}} selected',
      clearSelection: 'Clear selection',
      employeeStatus: 'Employee status',
      employeeStatuses: {
        ACTIVE: 'Active only',
        INACTIVE: 'Inactive only',
        ALL: 'Active and inactive',
      },
      documentTypes: 'Document types',
      allDocumentTypes: 'All sales documents',
      documentStatuses: 'Document statuses',
      allDocumentStatuses: 'All relevant statuses',
      phoneDirections: 'Call direction',
      allPhoneDirections: 'Inbound and outbound',
      phoneDirection: {
        INBOUND: 'Inbound',
        OUTBOUND: 'Outbound',
      },
      customerGroups: 'Customer groups',
      countryCodes: 'Countries',
      salesRegions: 'Sales territories',
      commaSeparated: 'Comma-separated',
      conversionWindow: 'Contact window (days)',
      includeUnassigned: 'Include unassigned data',
    },
    kpis: {
      netRevenue: {
        label: 'Team net revenue',
        definition:
          'Relevant invoice net amounts minus posted cancellations and credit notes; date: document date, attribution: auditable sales-representative snapshot.',
      },
      orderIntake: {
        label: 'Order intake',
        definition:
          'Net value of confirmed, active, partially processed or completed orders by document date and sales attribution.',
      },
      grossProfit: {
        label: 'Gross profit',
        definition:
          'Invoiced net revenue including negative corrections minus the frozen cost of goods in document items.',
      },
      totalCalls: {
        label: 'Calls',
        definition:
          'Unique CTI/Ringover entries from CommunicationLog by start time and CommunicationLog.userId. Manual CallLog entries are excluded.',
      },
      quoteToOrderConversionPct: {
        label: 'Quote conversion',
        definition:
          'Weighted team share of relevant quotes with a qualifying follow-up order through sourceDocumentId; no similarity estimation.',
      },
    },
    chart: {
      title: 'Revenue, order intake and telephony over time',
      netRevenue: 'Net revenue',
      orderIntake: 'Order intake',
      comparisonRevenue: 'Comparison net revenue',
      calls: 'CTI calls',
      callDuration: 'CTI call duration',
    },
    unassigned: {
      title: 'Unassigned documents',
      description:
        'These values have no reliable sales attribution and are deliberately not credited to a person. CTI calls are technically assigned to a user.',
    },
    table: {
      title: 'Employees ({{count}})',
      employee: 'Employee',
      status: 'Status',
      netRevenue: 'Invoiced net revenue',
      orderIntake: 'Order intake',
      grossProfit: 'Gross profit',
      margin: 'Margin',
      quotes: 'Quotes',
      orders: 'Orders',
      invoices: 'Invoices',
      quoteConversion: 'Quote conversion',
      averageOrderValue: 'Avg. order value',
      calls: 'Calls',
      callDuration: 'Call duration',
      reachability: 'Reachability',
      customers: 'Served customers',
      comparison: 'Period comparison',
      documents: 'Documents',
      documentSummary: '{{quotes}} quotes · {{orders}} orders · {{invoices}} invoices',
      columns: 'Columns',
      visibleColumns: 'Visible columns',
      openEmployee: 'Open details for {{name}}',
      page: 'Page {{page}} of {{pages}} · {{count}} entries',
      columnLabels: {
        isActive: 'Status',
        netRevenue: 'Invoiced net revenue',
        orderIntake: 'Order intake',
        grossProfit: 'Gross profit',
        marginPct: 'Margin',
        quoteCount: 'Quotes',
        orderCount: 'Orders',
        invoiceCount: 'Invoices',
        quoteToOrderConversionPct: 'Quote conversion',
        averageOrderValue: 'Avg. order value',
        totalCalls: 'Calls',
        totalCallDurationSeconds: 'Call duration',
        reachabilityPct: 'Reachability',
        servedCustomerCount: 'Served customers',
        comparisonDeltaPct: 'Period comparison',
      },
    },
    funnel: {
      QUOTE: 'Quotes',
      ORDER: 'Orders',
      DELIVERY_NOTE: 'Delivery notes',
      INVOICE: 'Invoices',
    },
    detail: {
      title: 'Employee details',
      description:
        'Aggregated business metrics without communication content or personality assessment.',
      updatedAt: 'Data as of {{date}}',
      development: 'Own trend and comparison period',
      funnel: 'Document funnel quote → order → delivery note → invoice',
      salesMetrics: 'Sales, revenue and document quality',
      telephony: 'Telephony aggregate',
      telephonyPrivacy:
        'Manually logged customer calls are shown separately. Audio, phone numbers, transcripts, content, summaries and sentiment are not processed here.',
      quality: 'Quality and corrections',
      customerActivities: 'Documented customer activity by channel',
      customerStructure: 'Customer structure',
      definitions: 'Metric definitions and data basis',
      inboundCalls: 'Inbound',
      outboundCalls: 'Outbound',
      answeredCalls: 'Answered',
      missedCalls: 'Missed (inbound)',
      notReachedCalls: 'Not reached (outbound)',
      failedCalls: 'Technically failed',
      callDuration: 'Total duration',
      averageCallDuration: 'Avg. answered call duration',
      unknownNumbers: 'Unassigned numbers',
      manualCalls: 'Manual customer calls',
      corrections: 'Cancellation/credit-note value',
      cancellationRate: 'Correction rate',
      fullyPaidRate: 'Fully paid',
      averageDiscount: 'Avg. discount rate',
      openFollowUps: 'Open follow-ups',
      overdueFollowUps: 'Overdue follow-ups',
      partnerAssigned: 'With business partner',
      technicallyProcessed: 'Technically completed',
      newCustomerRevenue: 'New-customer revenue',
      existingCustomerRevenue: 'Existing-customer revenue',
      newCustomers: 'New customers',
      reactivatedCustomers: 'Reactivated customers',
      customer: 'Customer',
      noCustomers: 'No reliably attributed customers in this period.',
      summary: {
        netRevenue: 'Net revenue',
        orderIntake: 'Order intake',
        grossProfit: 'Gross profit',
        marginPct: 'Margin',
        totalCalls: 'CTI calls',
        reachabilityPct: 'Reachability',
        servedCustomerCount: 'Served customers',
        netRevenueComparison: 'Revenue change',
      },
    },
    definitionLabels: {
      netRevenue: 'Invoiced net revenue',
      grossRevenue: 'Invoiced gross revenue',
      paidNetRevenue: 'Paid net revenue',
      openInvoiceAmount: 'Open invoice amount',
      overdueInvoiceAmount: 'Overdue invoice amount',
      correctionValue: 'Cancellation and credit-note value',
      correctionCount: 'Cancellation and correction documents',
      costOfGoods: 'Cost of goods',
      grossProfit: 'Gross profit',
      marginPct: 'Gross margin',
      averageInvoiceValue: 'Average invoice value',
      revenueSharePct: 'Share of team revenue',
      orderIntake: 'Order intake',
      quoteCount: 'Quotes',
      quoteValue: 'Net quote value',
      orderCount: 'Orders',
      orderValue: 'Net order value',
      deliveryCount: 'Delivery notes',
      deliveryValue: 'Net delivery-note value',
      invoiceCount: 'Posted invoices',
      averageOrderValue: 'Average order value',
      quoteToOrderConversionPct: 'Quote-to-order conversion',
      orderToInvoiceConversionPct: 'Order-to-invoice conversion',
      averageQuoteToOrderDays: 'Quote-to-order duration',
      averageOrderToInvoiceDays: 'Order-to-invoice duration',
      wonQuoteCount: 'Won quotes',
      lostQuoteCount: 'Lost quotes',
      averageDiscountPct: 'Average discount rate',
      cancellationRatePct: 'Correction rate',
      fullyPaidInvoiceRatePct: 'Share of fully paid invoices',
      totalCalls: 'CTI calls',
      inboundCalls: 'Inbound calls',
      outboundCalls: 'Outbound calls',
      answeredCalls: 'Answered calls',
      missedCalls: 'Missed inbound calls',
      notReachedCalls: 'Unreached outbound calls',
      failedCalls: 'Failed calls',
      voicemailCalls: 'Voicemail calls',
      reachabilityPct: 'Reachability rate',
      totalCallDurationSeconds: 'Total call duration',
      averageCallDurationSeconds: 'Average call duration',
      longestCallSeconds: 'Longest call',
      uniquePartnerContacts: 'Unique business-partner contacts',
      unknownPhoneNumbers: 'Unassigned phone numbers',
      callsPerWorkday: 'Calls per workday',
      manualCallCount: 'Manual customer calls',
      partnerAssignedCallPct: 'Calls with business partner',
      technicallyProcessedCallPct: 'Technically processed calls',
      servedCustomerCount: 'Served active customers',
      newCustomerCount: 'New customers',
      reactivatedCustomerCount: 'Reactivated customers',
      newCustomerRevenue: 'New-customer revenue',
      existingCustomerRevenue: 'Existing-customer revenue',
      revenuePerCustomer: 'Revenue per served customer',
      sentEmailCount: 'Sent documents and emails',
      sentWhatsAppCount: 'Sent WhatsApp messages',
      noteCount: 'Documented customer notes',
      openFollowUpCount: 'Open follow-ups',
      overdueFollowUpCount: 'Overdue follow-ups',
      callToQuoteConversionPct: 'Call-to-quote conversion',
      callToOrderConversionPct: 'Call-to-order conversion',
      netRevenueComparison: 'Revenue change versus comparison period',
    },
    definitions: {
      netRevenue:
        'Invoices in POSTED, COMPLETED or CANCELLED minus posted cancellations and credit notes. Drafts, deleted documents and isolated cancelled drafts are excluded. Document.documentDate and the auditable sales-attribution snapshot are authoritative.',
      grossRevenue:
        'The same qualifying population as invoiced net revenue, using Document.grossTotal including tax; posted cancellations and credit notes are deducted as negative gross corrections.',
      paidNetRevenue:
        'Net-revenue share proportional to the stored paidAmount relative to the gross amount. The data model has no reliable customer-payment timestamp, so average payment duration is not reported.',
      openInvoiceAmount:
        'Positive remaining gross amounts of relevant invoices with OPEN, PARTIALLY_PAID or OVERDUE at the current data state.',
      overdueInvoiceAmount:
        'Open gross balances whose dueDate precedes today in the tenant time zone.',
      correctionValue:
        'Net value of posted CANCELLATION and CREDIT_NOTE documents as a negative correction. The source invoice remains to preserve the auditable counter-entry.',
      correctionCount:
        'Number of posted CANCELLATION and CREDIT_NOTE documents by Document.documentDate and the auditable sales attribution.',
      costOfGoods:
        'DocumentItem.costPrice × quantityBase summed across revenue-relevant invoice items; costs on cancellation and credit-note items are reversed as negative values.',
      grossProfit:
        'Invoiced net revenue including corrections minus costPrice × quantityBase across the related document items.',
      marginPct:
        'Gross profit divided by invoiced net revenue × 100; undefined when revenue is zero.',
      averageInvoiceValue:
        'Sum of net values of relevant invoices before correction documents divided by the number of those invoices.',
      revenueSharePct:
        'The employee’s invoiced net revenue divided by net revenue of the currently filtered team, including visible unassigned values, multiplied by 100.',
      orderIntake:
        'Net value of orders in CONFIRMED, IN_PROGRESS, PARTIALLY_PROCESSED or COMPLETED by Document.documentDate.',
      quoteCount:
        'Number of quotes in SENT, ACCEPTED or REJECTED by Document.documentDate and the auditable sales-attribution snapshot.',
      quoteValue:
        'Sum of net values of these qualifying quotes by Document.documentDate and sales attribution.',
      orderCount:
        'Number of orders in CONFIRMED, IN_PROGRESS, PARTIALLY_PROCESSED or COMPLETED by Document.documentDate.',
      orderValue: 'Sum of net values of qualifying orders; this equals the reported order intake.',
      deliveryCount:
        'Number of delivery notes in SHIPPED or COMPLETED by Document.documentDate and sales attribution.',
      deliveryValue:
        'Sum of net values of delivery notes in SHIPPED or COMPLETED by Document.documentDate and sales attribution.',
      invoiceCount:
        'Number of invoices in POSTED or COMPLETED by Document.documentDate; correction documents are counted separately.',
      averageOrderValue:
        'Net value of qualifying orders divided by their count; zero when there is no order.',
      quoteToOrderConversionPct:
        'Share of relevant quotes with at least one qualifying order whose sourceDocumentId points exactly to the quote.',
      orderToInvoiceConversionPct:
        'Share of relevant orders with a qualifying invoice either directly or through a delivery note in the actual sourceDocumentId flow.',
      averageQuoteToOrderDays:
        'Average calendar days from quote document date to the earliest qualifying follow-up order in the document flow.',
      averageOrderToInvoiceDays:
        'Average calendar days from order document date to the earliest qualifying invoice, directly or through a delivery note.',
      wonQuoteCount:
        'Number of qualifying quotes with status ACCEPTED by Document.documentDate and sales attribution.',
      lostQuoteCount:
        'Number of qualifying quotes with status REJECTED by Document.documentDate and sales attribution.',
      averageDiscountPct:
        'Header and line discounts granted on qualifying orders divided by their pre-discount net value × 100.',
      cancellationRatePct:
        'Number of posted cancellation/credit-note documents divided by invoices plus correction documents × 100.',
      fullyPaidInvoiceRatePct:
        'Relevant invoices with PaymentStatus PAID divided by all relevant invoices × 100.',
      totalCalls:
        'Unique CommunicationLog records by startedAt in the tenant time zone and CommunicationLog.userId. CallLog is not merged.',
      inboundCalls:
        'CTI records with CommunicationLog.direction = INBOUND by startedAt and userId.',
      outboundCalls:
        'CTI records with CommunicationLog.direction = OUTBOUND by startedAt and userId.',
      answeredCalls: 'CTI records with CommunicationLog.status = ANSWERED.',
      missedCalls: 'Inbound CTI records with status MISSED.',
      notReachedCalls:
        'Outbound CTI records with status MISSED; these remain separate from missed inbound calls.',
      failedCalls: 'CTI records with CommunicationLog.status = FAILED.',
      voicemailCalls: 'CTI records with CommunicationLog.status = VOICEMAIL.',
      reachabilityPct:
        'Answered CTI calls divided by completed outcomes answered, missed, not reached, voicemail and failed × 100.',
      totalCallDurationSeconds:
        'Sum of CommunicationLog.durationSeconds across all CTI calls in the period; manual CallLog records are excluded.',
      averageCallDurationSeconds:
        'Average CommunicationLog.durationSeconds for answered CTI calls only.',
      longestCallSeconds:
        'The largest stored CommunicationLog.durationSeconds value in the selected period.',
      uniquePartnerContacts:
        'Number of distinct business partners resolved through CommunicationLog.partnerId.',
      unknownPhoneNumbers:
        'Number of distinct external phone numbers from CTI calls without a resolved CommunicationLog.partnerId; the numbers themselves are not returned.',
      callsPerWorkday:
        'CTI-call count divided by Mondays through Fridays in the selected tenant calendar range; public holidays are not assumed.',
      manualCallCount:
        'Separately counted manually documented customer calls from CallLog by occurredAt and userId; never merged with CTI records.',
      partnerAssignedCallPct:
        'CTI calls with a resolved CommunicationLog.partnerId divided by all CTI calls × 100.',
      technicallyProcessedCallPct:
        'CTI records with endedAt and a status other than RINGING divided by all CTI records × 100.',
      servedCustomerCount:
        'Distinct active business partners with a qualifying document or attributed CTI contact by the employee during the period.',
      newCustomerCount:
        'Distinct customers whose first qualifying invoice falls in the selected period and is attributed to the employee.',
      reactivatedCustomerCount:
        'Distinct customers with a new qualifying invoice after at least 180 calendar days without a qualifying invoice.',
      newCustomerRevenue:
        'Invoiced net revenue including corrections from customers whose first qualifying invoice falls within the selected period.',
      existingCustomerRevenue:
        'Invoiced net revenue including corrections from customers whose first qualifying invoice predates the selected period.',
      revenuePerCustomer:
        'Invoiced net revenue divided by served active customers; zero when there are no customers.',
      sentEmailCount:
        'Successful EmailLog records with status SENT by sentAt and sentByUserId; document and customer filters apply.',
      sentWhatsAppCount:
        'Outbound WhatsAppMessage records in SENT, DELIVERED or READ by sentAt or createdAt and senderUserId.',
      noteCount: 'CustomerNote records by occurredAt and authorId in the selected period.',
      openFollowUpCount:
        'Customer notes from the period with followUpAt and no followUpDoneAt at the current data state.',
      overdueFollowUpCount:
        'Open follow-ups from customer notes in the period whose followUpAt has passed.',
      callToQuoteConversionPct:
        'Share of CTI calls followed by a qualifying quote for the same business partner and employee within the configured window. This is temporal attribution, not proven causality.',
      callToOrderConversionPct:
        'Share of CTI calls followed by a qualifying order for the same business partner and employee within the configured window. This is temporal attribution, not proven causality.',
      netRevenueComparison:
        'Percentage change in invoiced net revenue versus the previous period or the same period last year. No percentage is defined when the comparison value is zero.',
    },
  },
};
